import React, { useEffect, useState } from 'react';
import { useLayout } from '../DashboardLayout/DashboardLayout';
import './DashboardSidebar.css';
import { useAuth } from '../../../context/AuthContext';
import { NavLink, useMatch } from 'react-router-dom';
import { collection, doc, getDoc, getFirestore } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';

function DashboardSidebar() {
  const { isCollapsed, setIsCollapsed } = useLayout();
  const { claims } = useAuth();
  const [user, setUser] = useState({});
  const matchCalendar = useMatch('/calendar');
  const matchEditCalendar = useMatch('/editCalendar');

  const hasAccess = (routeClaims) => {
    if (routeClaims.userForbidden === false) {
      return true;
    }
    if ('superadmin' in claims && claims.superadmin) {
      return true;
    }
    if ('admin' in claims && claims.admin) {
      return true;
    }
    if ('staff' in claims && claims.staff) {
      return true;
    }
    return false;
  };
  const groupAccess = () => {
    if ('superadmin' in claims && claims.superadmin) {
      return true;
    }
    if (
      'admin' in claims &&
      claims.admin &&
      user?.adminPermissions?.includes('groupsAdmin')
    ) {
      return true;
    }
    if (
      'staff' in claims &&
      claims.staff &&
      user?.staffPermissions?.includes('groupsAdmin')
    ) {
      return true;
    }
    return false;
  };
  useEffect(() => {
    // get user data from firebase
    const getUserData = async () => {
      const db = getFirestore();
      if (claims.user_id) {
        const userDocRef = doc(collection(db, 'users'), claims.user_id);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          setUser(userDoc.data());
        }
      }
    };
    getUserData();
  }, [claims]);

  const location = useLocation();

  const isPathMatch = (paths) =>
    paths.some((path) => location.pathname.startsWith(path));

  const isLargeScreen = () => window.innerWidth > 768;

  const [dropdowns, setDropdowns] = useState({
    quickCareCallLights: isLargeScreen() && isPathMatch(['/groups', '/rooms']),
    calendarDropdown:
      isLargeScreen() &&
      isPathMatch(['/calendar', '/editCalendar', '/corporate-menu']),
  });

  useEffect(() => {
    if (isLargeScreen()) {
      setDropdowns({
        quickCareCallLights: isPathMatch(['/groups', '/rooms']),
        calendarDropdown: isPathMatch([
          '/calendar',
          '/editCalendar',
          '/corporate-menu',
        ]),
        gallerySubmitImages: isPathMatch(['/gallery', '/submit']),
      });
    } else {
      setDropdowns({
        quickCareCallLights: false,
        calendarDropdown: false,
      });
    }
  }, [location.pathname]);

  const toggleDropdown = (dropdown) => {
    setDropdowns((prevState) => ({
      ...prevState,
      [dropdown]: !prevState[dropdown],
    }));
  };

  const closeAllDropdowns = () => {
    setDropdowns({
      quickCareCallLights: false,
      calendarDropdown: false,
    });
  };

  return (
    <div className="dashboardSidebarContainer">
      <div className="dashboardSidebarWrapper">
        {hasAccess({ userForbidden: false }) && (
          <div className="dropdownContainer">
            <button
              className="dashboardSidebarLink dashboardSidebarButton"
              onClick={() => toggleDropdown('gallerySubmitImages')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                <path d="M160 32c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H160zM396 138.7l96 144c4.9 7.4 5.4 16.8 1.2 24.6S480.9 320 472 320H328 280 200c-9.2 0-17.6-5.3-21.6-13.6s-2.9-18.2 2.9-25.4l64-80c4.6-5.7 11.4-9 18.7-9s14.2 3.3 18.7 9l17.3 21.6 56-84C360.5 132 368 128 376 128s15.5 4 20 10.7zM192 128a32 32 0 1 1 64 0 32 32 0 1 1 -64 0zM48 120c0-13.3-10.7-24-24-24S0 106.7 0 120V344c0 75.1 60.9 136 136 136H456c13.3 0 24-10.7 24-24s-10.7-24-24-24H136c-48.6 0-88-39.4-88-88V120z" />
              </svg>
              <span>Gallery</span>

              <svg
                className={`dropdownSVG ${dropdowns.gallerySubmitImages ? 'open' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
              >
                <path d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
              </svg>
            </button>
            {dropdowns.gallerySubmitImages && (
              <div className="dropdown dropdownGallery">
                <NavLink
                  to="/submit"
                  className="dashboardSidebarLink"
                  activeclassname="active"
                >
                  <span>Submit Images</span>
                </NavLink>
                <NavLink
                  to="/gallery"
                  className="dashboardSidebarLink"
                  activeclassname="active"
                >
                  <span>Gallery</span>
                </NavLink>
              </div>
            )}
          </div>
        )}

        <div className="dropdownContainer">
          <button
            className="dashboardSidebarLink dashboardSidebarButton"
            onClick={() => toggleDropdown('calendarDropdown')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
              <path d="M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zm64 80v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm128 0v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H208c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H336zM64 400v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H208zm112 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V400c0-8.8-7.2-16-16-16H336c-8.8 0-16 7.2-16 16z" />
            </svg>
            <span>Calendar</span>

            <svg
              className={`dropdownSVG ${dropdowns.calendarDropdown ? 'open' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
            </svg>
          </button>
          {dropdowns.calendarDropdown && (
            <div className="dropdown dropdownCalendar">
              {hasAccess({ userForbidden: false }) && (
                <NavLink
                  to="/calendar"
                  className="dashboardSidebarLink "
                  activeclassname="active"
                >
                  <span>Calendar</span>
                </NavLink>
              )}
              {hasAccess({ userForbidden: true }) && (
                <NavLink
                  to="/editCalendar"
                  className="dashboardSidebarLink "
                  activeclassname="active"
                >
                  <span>Calendar Editor</span>
                </NavLink>
              )}
              {claims?.superadmin && (
                <NavLink
                  to="/corporate-menu"
                  className="dashboardSidebarLink "
                  activeclassname="active"
                >
                  <span>Corporate Menu</span>
                </NavLink>
              )}
            </div>
          )}
        </div>

        {hasAccess({ userForbidden: true }) && (
          <NavLink
            to="/scheduler"
            className="dashboardSidebarLink"
            activeclassname="active"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path d="M64 256l0-96 160 0 0 96L64 256zm0 64l160 0 0 96L64 416l0-96zm224 96l0-96 160 0 0 96-160 0zM448 256l-160 0 0-96 160 0 0 96zM64 32C28.7 32 0 60.7 0 96L0 416c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32z" />
            </svg>
            <span>Daily Schedule</span>
          </NavLink>
        )}

        {hasAccess({ userForbidden: true }) && (
          <NavLink
            to="/accounts"
            className="dashboardSidebarLink"
            activeclassname="active"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
              <path d="M96 128a128 128 0 1 0 256 0A128 128 0 1 0 96 128zm94.5 200.2l18.6 31L175.8 483.1l-36-146.9c-2-8.1-9.8-13.4-17.9-11.3C51.9 342.4 0 405.8 0 481.3c0 17 13.8 30.7 30.7 30.7H162.5c0 0 0 0 .1 0H168 280h5.5c0 0 0 0 .1 0H417.3c17 0 30.7-13.8 30.7-30.7c0-75.5-51.9-138.9-121.9-156.4c-8.1-2-15.9 3.3-17.9 11.3l-36 146.9L238.9 359.2l18.6-31c6.4-10.7-1.3-24.2-13.7-24.2H224 204.3c-12.4 0-20.1 13.6-13.7 24.2z" />
            </svg>
            <span>Account Manager</span>
          </NavLink>
        )}

        {hasAccess({ userForbidden: true }) && (
          <NavLink
            to="/devices"
            className="dashboardSidebarLink"
            activeclassname="active"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
              <path d="M80 0C44.7 0 16 28.7 16 64V448c0 35.3 28.7 64 64 64H304c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H80zM192 400a32 32 0 1 1 0 64 32 32 0 1 1 0-64z" />
            </svg>

            <span>Device Manager</span>
          </NavLink>
        )}

        {hasAccess({ userForbidden: true }) && (
          <NavLink
            to="/signage"
            className="dashboardSidebarLink"
            activeclassname="active"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160L0 416c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-96c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7-14.3 32-32 32L96 448c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 64z" />
            </svg>
            <span>Signage Editor</span>
          </NavLink>
        )}

        {hasAccess({ userForbidden: true }) && (
          <NavLink
            to="/testimonials"
            className="dashboardSidebarLink"
            activeclassname="active"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path d="M4.1 38.2C1.4 34.2 0 29.4 0 24.6C0 11 11 0 24.6 0L133.9 0c11.2 0 21.7 5.9 27.4 15.5l68.5 114.1c-48.2 6.1-91.3 28.6-123.4 61.9L4.1 38.2zm503.7 0L405.6 191.5c-32.1-33.3-75.2-55.8-123.4-61.9L350.7 15.5C356.5 5.9 366.9 0 378.1 0L487.4 0C501 0 512 11 512 24.6c0 4.8-1.4 9.6-4.1 13.6zM80 336a176 176 0 1 1 352 0A176 176 0 1 1 80 336zm184.4-94.9c-3.4-7-13.3-7-16.8 0l-22.4 45.4c-1.4 2.8-4 4.7-7 5.1L168 298.9c-7.7 1.1-10.7 10.5-5.2 16l36.3 35.4c2.2 2.2 3.2 5.2 2.7 8.3l-8.6 49.9c-1.3 7.6 6.7 13.5 13.6 9.9l44.8-23.6c2.7-1.4 6-1.4 8.7 0l44.8 23.6c6.9 3.6 14.9-2.2 13.6-9.9l-8.6-49.9c-.5-3 .5-6.1 2.7-8.3l36.3-35.4c5.6-5.4 2.5-14.8-5.2-16l-50.1-7.3c-3-.4-5.7-2.4-7-5.1l-22.4-45.4z" />
            </svg>
            <span>Testimonials</span>
          </NavLink>
        )}

        <div className="lineSeparator">
          <div className="inDevelopment">In Development</div>
        </div>

        {hasAccess({ userForbidden: true }) && (
          <div className="dropdownContainer">
            <button
              className="dashboardSidebarLink dashboardSidebarButton"
              onClick={() => toggleDropdown('quickCareCallLights')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M224 0c-17.7 0-32 14.3-32 32l0 19.2C119 66 64 130.6 64 208l0 18.8c0 47-17.3 92.4-48.5 127.6l-7.4 8.3c-8.4 9.4-10.4 22.9-5.3 34.4S19.4 416 32 416l384 0c12.6 0 24-7.4 29.2-18.9s3.1-25-5.3-34.4l-7.4-8.3C401.3 319.2 384 273.9 384 226.8l0-18.8c0-77.4-55-142-128-156.8L256 32c0-17.7-14.3-32-32-32zm45.3 493.3c12-12 18.7-28.3 18.7-45.3l-64 0-64 0c0 17 6.7 33.3 18.7 45.3s28.3 18.7 45.3 18.7s33.3-6.7 45.3-18.7z" />
              </svg>
              <span>QuickCare</span>

              <svg
                className={`dropdownSVG ${dropdowns.quickCareCallLights ? 'open' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
              >
                <path d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
              </svg>
            </button>
            {dropdowns.quickCareCallLights && (
              <div className="dropdown dropdownQuickCare">
                {groupAccess() && (
                  <NavLink
                    to="/groups"
                    className="dashboardSidebarLink"
                    activeclassname="active"
                  >
                    <span>Group Editor</span>
                  </NavLink>
                )}
                {hasAccess({ userForbidden: true }) && (
                  <NavLink
                    to="/rooms"
                    className="dashboardSidebarLink"
                    activeclassname="active"
                  >
                    <span>Room Manager</span>
                  </NavLink>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <button
        className={`dashboardSidebarLink SidebarCollapse`}
        onClick={() => {
          setIsCollapsed(!isCollapsed);
          closeAllDropdowns();
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512">
          <path d="M9.4 278.6c-12.5-12.5-12.5-32.8 0-45.3l128-128c9.2-9.2 22.9-11.9 34.9-6.9s19.8 16.6 19.8 29.6l0 256c0 12.9-7.8 24.6-19.8 29.6s-25.7 2.2-34.9-6.9l-128-128z" />
        </svg>
        <span>Collapse</span>
      </button>
    </div>
  );
}

export default DashboardSidebar;
